import { Link, Text } from '@react-pdf/renderer'
import type { Style } from '@react-pdf/stylesheet'
import type { InlineNode } from 'features/knowledgeDocumentViewer/artifacts/freeformMarkdown'
import React from 'react'

import { PdfMissing } from './PdfMissing'
import type { PdfStyles } from './PdfPrimitives'

interface InlineFormat {
  bold: boolean
  italic: boolean
  strike: boolean
}

const PLAIN: InlineFormat = { bold: false, italic: false, strike: false }

function inlineStyle(styles: PdfStyles, format: InlineFormat): Style[] {
  const out: Style[] = []
  if (format.bold && format.italic) out.push(styles.boldItalic)
  else if (format.bold) out.push(styles.bold)
  else if (format.italic) out.push(styles.italic)
  if (format.strike) out.push(styles.strike)
  return out
}

export function renderInline(
  styles: PdfStyles,
  nodes: readonly InlineNode[],
  format: InlineFormat = PLAIN
): React.ReactNode[] {
  return nodes.map((node, index) => {
    if (node.type === 'missing')
      return <PdfMissing key={index} styles={styles} />
    if (node.type === 'code') {
      return (
        <Text key={index} style={[styles.mono, ...inlineStyle(styles, format)]}>
          {node.text}
        </Text>
      )
    }
    if (node.type === 'text') {
      return (
        <Text key={index} style={inlineStyle(styles, format)}>
          {node.text}
        </Text>
      )
    }
    if (node.type === 'link') {
      return (
        <React.Fragment key={index}>
          <Link src={node.url} style={styles.link}>
            {renderInline(styles, node.children, format)}
          </Link>
          <Text style={styles.muted}> ({node.url})</Text>
        </React.Fragment>
      )
    }
    return (
      <React.Fragment key={index}>
        {renderInline(styles, node.children, {
          bold: format.bold || node.type === 'strong',
          italic: format.italic || node.type === 'em',
          strike: format.strike || node.type === 'del',
        })}
      </React.Fragment>
    )
  })
}
